// Collapses the live transcript's two feeds into one bubble per spoken turn.
//
// The call adapter receives transcript text on two paths that disagree on
// GRANULARITY, not just timing:
//
//   - RoomEvent.TranscriptionReceived finalises a segment per speech CHUNK, so a
//     caller spelling an email aloud produces "C a m i l a dot" and then
//     "w h i t e at g mail dot com." as two separate `final` segments.
//   - The voiceX data channel (and the phone WS) publishes the whole utterance
//     once: "C a m i l a dot w h i t e at g mail dot com.".
//
// Comparing whole strings can never match a fragment against its aggregate, so
// every one of those rendered as its own bubble.
//
// Instead, group a speaker's run into one OPEN utterance carrying a stable id
// and re-emit it as it grows. The reducer upserts on that id, so the bubble is
// rewritten in place rather than spawning siblings. Whichever feed is more
// complete wins: an aggregate containing what we hold replaces it, a fragment of
// what we hold is dropped, anything else is appended as the next chunk.
//
// Kept free of livekit-client and React on purpose — this is the part worth
// testing, and transcriptTurns.test.ts pins the behaviour.

export type TurnSpeaker = "agent" | "user" | "system";

export interface AccumulatedTurn {
  /** Stable across every re-emission of the same utterance. */
  id: string;
  speaker: TurnSpeaker;
  text: string;
  timestampMs: number;
}

/** An identical-text twin can arrive this long after the first copy. */
export const DEDUPE_WINDOW_MS = 60000;
/** Quiet gap from the same speaker that closes the open utterance. */
export const UTTERANCE_GAP_MS = 8000;

// Comparison key only — the emitted turn always keeps its original text.
// Strips to alphanumerics so spacing and punctuation differences between the two
// feeds ("C a m i l a dot" vs "Camila dot.") cannot defeat containment.
export function normalizeForCompare(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

let turnSeq = 0;
// crypto.randomUUID needs a secure context; fall back so a plain-http preview
// still renders a transcript instead of throwing.
function newTurnId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  turnSeq += 1;
  return `turn-${Date.now()}-${turnSeq}`;
}

export class TurnAccumulator {
  private open:
    | { id: string; speaker: TurnSpeaker; text: string; norm: string; lastTs: number }
    | null = null;
  private recent: { key: string; ts: number }[] = [];

  /**
   * Feed one piece of transcript text.
   * @returns the turn to render — a NEW id to append, a KNOWN id to rewrite in
   *          place — or null when the input added nothing (a duplicate or a
   *          fragment of what is already shown).
   */
  push(speaker: TurnSpeaker, rawText: string, now: number): AccumulatedTurn | null {
    const text = rawText.trim().replace(/\s+/g, " ");
    if (!text) return null;
    const norm = normalizeForCompare(text);
    if (!norm) return null;

    const open = this.open;
    const continues = !!open && open.speaker === speaker && now - open.lastTs < UTTERANCE_GAP_MS;

    if (open && continues) {
      let merged: string;
      if (norm === open.norm) {
        open.lastTs = now;                 // the other feed repeating what we rendered
        return null;
      } else if (norm.includes(open.norm)) {
        merged = text;                     // the aggregate — it supersedes the fragments
      } else if (open.norm.includes(norm)) {
        open.lastTs = now;                 // a fragment of what we already hold
        return null;
      } else {
        merged = `${open.text} ${text}`;   // the next chunk of the same utterance
      }
      open.text = merged;
      open.norm = normalizeForCompare(merged);
      open.lastTs = now;
      this.recent.push({ key: `${speaker}:${open.norm}`, ts: now });
      return { id: open.id, speaker, text: merged, timestampMs: now };
    }

    // Starting a new bubble. The identical twin can arrive after the other party
    // has spoken — which closed the utterance — so the wider window still has to
    // catch it. Short turns stay exempt so a caller legitimately repeating "yes"
    // later in the call still shows.
    this.recent = this.recent.filter((r) => now - r.ts < DEDUPE_WINDOW_MS);
    const key = `${speaker}:${norm}`;
    if (norm.length >= 8 && this.recent.some((r) => r.key === key)) return null;

    const id = newTurnId();
    this.open = { id, speaker, text, norm, lastTs: now };
    this.recent.push({ key, ts: now });
    return { id, speaker, text, timestampMs: now };
  }
}
